<script setup>
import { ref, computed, onMounted } from 'vue'

import { kBlock } from 'konsta/vue';

import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'
import { XMarkIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  availability: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:checkin', 'update:checkout', 'range-selected', 'month-change'])

const today = new Date()
today.setHours(0, 0, 0, 0)

const range = ref(null)

// --- Summary ---
const checkin  = computed(() => range.value?.[0] ?? null)
const checkout = computed(() => range.value?.[1] ?? null)

const nightsCount = computed(() => {
  if (!checkin.value || !checkout.value) return 0
  return Math.round((checkout.value - checkin.value) / (1000 * 60 * 60 * 24))
})

function formatDateLabel(date) {
  if (!date) return '—'
  return date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })
}

// --- Disponibilità ---
const availabilityMap = computed(() => {
  const map = {}
  props.availability.forEach(a => { map[a.date] = a.status })
  return map
})

function toKey(date) {
  if (!date) return null
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function getStatus(date) {
  const key = toKey(date)
  if (!key) return 'available'
  return availabilityMap.value[key] ?? 'available'
}

const partial_checkin = ref(null)

// giorni bloccati come notte, per stato
const blockedKeys = computed(() => {
  const set = {}
  props.availability.forEach(a => { set[a.date] = a.status })
  return set
})

function isBlocked(key) {
  return !!blockedKeys.value[key]
}

function isClosed(key) {
  return blockedKeys.value[key] === 'closed'
}

function keyToDate(key) {
  const p = key.split('-')
  return new Date(parseInt(p[0]), parseInt(p[1]) - 1, parseInt(p[2]))
}

function addDays(date, n) {
  const d = new Date(date)
  d.setDate(d.getDate() + n)
  return d
}

const disabledDates = computed(() => {
  // nessun check-in scelto: si bloccano tutte le notti non libere
  if (!partial_checkin.value) {
    return Object.keys(blockedKeys.value).map(keyToDate)
  }

  // check-in scelto: si abilita fino alla prima notte bloccata inclusa,
  // ma una chiusura blocca del tutto (non ci si può nemmeno uscire)
  const disabled = []
  let cursor = new Date(partial_checkin.value)
  let stop = false

  for (let i = 0; i < 400; i++) {
    cursor = addDays(cursor, 1)
    const key = toKey(cursor)

    if (stop) {
      disabled.push(new Date(cursor))
      continue
    }

    if (isClosed(key)) {
      // chiusura: non selezionabile nemmeno come check-out
      disabled.push(new Date(cursor))
      stop = true
    } else if (isBlocked(key)) {
      // notte occupata o senza disponibilità: usabile come check-out, poi stop
      stop = true
    }
  }

  // tutto ciò che precede il check-in resta bloccato come prima
  Object.keys(blockedKeys.value).forEach(k => {
    if (keyToDate(k) < partial_checkin.value) disabled.push(keyToDate(k))
  })

  return disabled
})

function onInternalChange(value) {
  if (Array.isArray(value) && value[0] && !value[1]) partial_checkin.value = value[0]
  else if (!value || !value[0]) partial_checkin.value = null
}

// --- Events ---
function onViewChange({ month, year }) {
  emit('month-change', { year, month: month + 1 })
}

function onRangeSelect(value) {
  if (!value || !value[0]) return

  const checkin = toKey(value[0])
  const checkout = value[1] ? toKey(value[1]) : null

  emit('update:checkin', checkin)
  emit('update:checkout', checkout)

  if (checkin && checkout) {
    emit('range-selected', { checkin, checkout })
  }
}

onMounted(() => {
  const d = range.value?.[0] ?? today
  emit('month-change', { year: d.getFullYear(), month: d.getMonth() + 1 })
})

function clearDates() {
  range.value = null
  partial_checkin.value = null

  emit('update:checkin', null)
  emit('update:checkout', null)
}
</script>

<template>
  <k-block>

    <!-- Summary check-in / check-out / notti -->
    <div class="flex gap-3 mb-4">
      <div :class="[
        'flex-1 rounded-xl border-2 px-3 py-2 transition-colors',
        checkin ? 'border-primary bg-primary/5' : 'border-gray-200 bg-gray-50'
      ]">
        <div class="text-xs text-gray-400 mb-0.5">Check-in</div>
        <div :class="['text-sm font-semibold', checkin ? 'text-primary' : 'text-gray-300']">
          {{ formatDateLabel(checkin) }}
        </div>
      </div>

      <div :class="[
        'flex-1 rounded-xl border-2 px-3 py-2 transition-colors',
        checkout ? 'border-primary bg-primary/5' : 'border-gray-200 bg-gray-50'
      ]">
        <div class="text-xs text-gray-400 mb-0.5">Check-out</div>
        <div :class="['text-sm font-semibold', checkout ? 'text-primary' : 'text-gray-300']">
          {{ formatDateLabel(checkout) }}
        </div>
      </div>

      <div
        v-if="nightsCount > 0"
        class="flex items-center justify-center px-3 rounded-xl bg-primary text-white min-w-[56px]">
        <div class="text-center">
          <div class="text-lg font-bold leading-none">{{ nightsCount }}</div>
          <div class="text-xs">{{ nightsCount === 1 ? 'notte' : 'notti' }}</div>
        </div>
      </div>

      <button
        v-if="checkin || checkout"
        type="button"
        class="flex items-center justify-center px-3 rounded-xl border-2 border-gray-200 text-gray-400"
        @click="clearDates">
        <XMarkIcon class="w-5 h-5"/>
      </button>
    </div>

    <!-- Calendario -->
    <VueDatePicker
      v-model="range"
      range
      :no-disabled-range="true"
      :inline="{ input: false }"
      :enable-time-picker="false"
      auto-apply
      locale="it"
      :min-date="today"
      :disabled-dates="disabledDates"
      class="w-full block"
      @update:model-value="onRangeSelect"
      @internal-model-change="onInternalChange"
      @update-month-year="onViewChange"
    >
      <template #day="{ day, date }">
        <span :class="{
          'bnb-day-occupied': getStatus(date) === 'occupied',
          'bnb-day-closed': getStatus(date) === 'closed',
        }">{{ day }}</span>
      </template>
    </VueDatePicker>

  </k-block>
</template>

<style lang="scss">
.dp__theme_light {
  --dp-primary-color: var(--color-primary);
  --dp-background-color: transparent;
  --dp-menu-border-color: transparent;
  --dp-cell-border-radius: 50%;

  --dp-hover-color: color-mix(in srgb, var(--color-primary) 15%, white);
  --dp-range-between-dates-background-color: color-mix(in srgb, var(--color-primary) 15%, white);
  --dp-range-between-dates-text-color: var(--color-primary);
  --dp-range-between-border-color: transparent;
}

.dp__calendar_item:has(.dp__range_start) {
  background-color: color-mix(in srgb, var(--color-primary) 15%, white);
}

.dp__calendar_item:has(.dp__range_between) {
  background-color: color-mix(in srgb, var(--color-primary) 15%, white);
}

// .dp__range_start {}

// .dp__range_end {}

// .dp__range_between {}

.dp__cell_disabled:has(.bnb-day-occupied) {
  border-color: #f87171;
  border-radius: 50%;
  color: #f87171;
}

.dp__cell_disabled:has(.bnb-day-closed) {
  border-color: #d1d5db;
  border-radius: 50%;
  color: #d1d5db;
}
</style>